"use client"

import { useEffect, useState } from "react"
import Link from "next/link"

interface StickyJoinCTAProps {
  size?: 'small' | 'medium' | 'large'
}

const sizeConfig = {
  small: {
    container: 'pl-4 pr-2 py-2',
    gap: 'gap-2',
    text: 'text-sm',
    button: 'px-3 py-1.5 text-sm',
    dismissIcon: '14',
    dismissPadding: 'p-0.5'
  },
  medium: {
    container: 'pl-6 pr-3 py-3',
    gap: 'gap-3',
    text: 'text-base',
    // Match hero CTA style
    button: 'px-5 py-2.5 text-sm sm:text-base',
    dismissIcon: '16',
    dismissPadding: 'p-1'
  },
  large: {
    container: 'pl-8 pr-4 py-4',
    gap: 'gap-4',
    text: 'text-lg',
    button: 'px-8 py-4 text-lg',
    dismissIcon: '20',
    dismissPadding: 'p-1.5'
  }
}

/**
 * Subtle floating CTA that appears after the hero when users scroll.
 * Designed to match the site's look (white pill, navy text) and stay out of the way.
 */
export default function StickyJoinCTA({ size = 'medium' }: StickyJoinCTAProps) {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const config = sizeConfig[size]

  useEffect(() => {
    try {
      const v = localStorage.getItem("bc-cta-dismissed")
      if (v === "1") setDismissed(true)
    } catch {}

    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop
      // Show after user has engaged with the page a bit
      setVisible(y > 250)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  if (dismissed || !visible) return null

  return (
    <div
      className="fixed right-4 sm:right-6 bottom-[calc(env(safe-area-inset-bottom)+16px)] sm:bottom-6 z-[60]"
      role="dialog"
      aria-label="Join as an expert"
    >
      <div className={`flex items-center ${config.gap} bg-white text-[#2a3550] border border-[#2a3550]/15 rounded-full ${config.container} shadow-lg hover:shadow-xl transition-shadow`}>
        <span className={`hidden sm:block ${config.text} font-medium`}>Are you an expert?</span>
        <Link
          href="/signup"
          className={`inline-flex items-center rounded-xl bg-[#2a3550] text-white ${config.button} font-semibold uppercase tracking-wide shadow-lg hover:bg-[#243147] focus:outline-none focus:ring-2 focus:ring-[#2a3550]/30`}
        >
          Join as an Expert
        </Link>
        <button
          aria-label="Dismiss"
          className={`ml-1 rounded-full ${config.dismissPadding} text-[#2a3550]/60 hover:text-[#2a3550] focus:outline-none`}
          onClick={() => {
            setDismissed(true)
            try { localStorage.setItem("bc-cta-dismissed", "1") } catch {}
          }}
        >
          <svg width={config.dismissIcon} height={config.dismissIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}